import React, { useEffect, useMemo, useState } from 'react';
import {
  formatTimerClock,
  getStepPacingTimerState,
  normalizeStepPacingTimer,
  PacingTimerRuntimeState,
} from '../../utils/pacingTimer';

interface PacingTimerCardProps {
  content: any;
  startedAt?: string | null;
  title?: string;
  runtimeState?: PacingTimerRuntimeState | null;
  showControls?: boolean;
  actionLoading?: boolean;
  onAction?: (action: 'start' | 'restart' | 'add' | 'end', seconds?: number) => void;
}

export const PacingTimerCard: React.FC<PacingTimerCardProps> = ({
  content,
  startedAt,
  title = '',
  runtimeState,
  showControls = false,
  actionLoading = false,
  onAction,
}) => {
  const [nowMs, setNowMs] = useState(Date.now());
  const authoredConfig = useMemo(() => normalizeStepPacingTimer(content), [content]);
  const runtimeContent = useMemo(() => {
    if (!runtimeState) return content;
    return {
      ...(content && typeof content === 'object' ? content : {}),
      pacing_timer: {
        ...authoredConfig,
        enabled: true,
        duration_seconds: runtimeState.duration_seconds,
        label: runtimeState.label || authoredConfig.label,
      },
    };
  }, [authoredConfig, content, runtimeState]);
  const resolvedStartedAt = runtimeState
    ? (runtimeState.status === 'running' ? runtimeState.started_at : null)
    : startedAt;
  const timerState = useMemo(
    () => getStepPacingTimerState(runtimeContent, resolvedStartedAt, nowMs),
    [runtimeContent, resolvedStartedAt, nowMs],
  );

  useEffect(() => {
    if (!timerState) return undefined;
    setNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [timerState?.duration_seconds, timerState?.started_at_ms]);

  const runtimeReady = runtimeState?.status === 'ready';
  const runtimeEnded = runtimeState?.status === 'ended';
  if (!timerState && !runtimeReady && !runtimeEnded) return null;

  const borderClass = runtimeReady
    ? 'border-sky-200 bg-sky-50 text-sky-950'
    : runtimeEnded || timerState?.expired
    ? 'border-rose-200 bg-rose-50 text-rose-900'
    : timerState?.warning
      ? 'border-amber-200 bg-amber-50 text-amber-950'
      : 'border-sky-200 bg-sky-50 text-sky-950';
  const eyebrowClass = runtimeEnded || timerState?.expired
    ? 'text-rose-700'
    : timerState?.warning
      ? 'text-amber-700'
      : 'text-sky-700';
  const statusText = runtimeReady
    ? 'Prompt is open · waiting for teacher'
    : runtimeEnded || timerState?.expired
    ? 'Time to check progress'
    : timerState?.warning
      ? 'Progress check soon'
      : 'Work toward your checkpoint';
  const displayTitle = runtimeState?.label || timerState?.label || title || 'Current step';
  const displayedSeconds = runtimeReady
    ? runtimeState?.duration_seconds || authoredConfig.duration_seconds
    : runtimeEnded
      ? 0
      : timerState?.remaining_seconds || 0;

  return (
    <div className={`rounded-2xl border px-4 py-3 ${borderClass}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className={`text-[11px] font-semibold uppercase tracking-[0.2em] ${eyebrowClass}`}>Pacing timer</p>
          <p className="mt-1 text-sm font-medium">{displayTitle}</p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-semibold tabular-nums">{formatTimerClock(displayedSeconds)}</p>
          <p className="mt-1 text-xs">{statusText}</p>
        </div>
      </div>
      {showControls && onAction ? (
        <div className="mt-3 flex flex-wrap gap-2 border-t border-current/10 pt-3">
          {runtimeReady || runtimeEnded ? (
            <button
              type="button"
              className="rounded bg-sky-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              onClick={() => onAction(runtimeEnded ? 'restart' : 'start')}
              disabled={actionLoading}
            >
              {runtimeEnded ? 'Restart timer' : 'Start timer'}
            </button>
          ) : (
            <>
              <button type="button" className="rounded border border-sky-300 bg-white px-3 py-1.5 text-xs font-medium" onClick={() => onAction('add', 30)} disabled={actionLoading}>+30 sec</button>
              <button type="button" className="rounded border border-sky-300 bg-white px-3 py-1.5 text-xs font-medium" onClick={() => onAction('add', 60)} disabled={actionLoading}>+1 min</button>
              <button type="button" className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium" onClick={() => onAction('restart')} disabled={actionLoading}>Restart</button>
              <button type="button" className="rounded border border-rose-300 bg-white px-3 py-1.5 text-xs font-medium text-rose-700" onClick={() => onAction('end')} disabled={actionLoading}>End now</button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};
